<template>
    <div class="container">
        <span @click="$emit('close')" role="button"><small><i
                    class="fa-solid fa-chevron-left me-2"></i>{{ $t('common.back') }}</small></span>
        <h2 class="my-3">{{ type === 'materials' ? $t('taskDetail.addMaterial') : $t('taskDetail.addTime') }}</h2>

        <div class="mb-3">
            <label for="resourceSelect" class="form-label"><small>{{ type === 'materials' ? $t('modal.material') : $t('taskDetail.work')
                    }}</small></label>
            <!-- The scan button sits alongside the select rather than beside an SN
                 text field: a decoded serial picks the stock item, it is not stored
                 as free text. -->
            <div v-if="!itemToEdit" class="d-flex align-items-start gap-2">
                <v-select id="resourceSelect" v-model="selectedResource" :options="resources"
                    label="name" :reduce="resource => resource.id" :filter-by="filterResource"
                    class="bg-body-tertiary flex-grow-1">
                    <template #option="option">
                        {{ option.name }}<span v-if="option.quantity != null" class="text-body-secondary ms-1">({{
                            formatQuantity(option.quantity) }})</span>
                    </template>
                    <template #selected-option="option">
                        {{ option.name }}<span v-if="option.quantity != null" class="text-body-secondary ms-1">({{
                            formatQuantity(option.quantity) }})</span>
                    </template>
                </v-select>
                <button v-if="type === 'materials'" type="button" class="btn-icon bg-primary-subtle text-primary-emphasis flex-shrink-0"
                    :title="$t('modal.scanSerial')" :aria-label="$t('modal.scanSerial')" @click="openScanner">
                    <i class="fa-solid fa-camera"></i>
                </button>
            </div>
            <div v-else class="form-select bg-secondary-subtle">{{ itemToEdit.resource_name }}</div>
        </div>

        <barcode-scanner v-if="scannerVisible" ref="scanner"
            :formats="['qr_code', 'code_128', 'ean_8', 'ean_13']" @decode="onScannedValue" @close="closeScanner">
            <template #footer>
                <div v-if="scanNoMatch" class="alert alert-danger mt-3 mb-0 text-start">
                    <div>{{ $t('modal.scanNoMatch', { sn: scanNoMatch }) }}</div>
                    <button type="button" class="btn btn-sm btn-outline-danger mt-2" @click="copyScanned">
                        <i class="fa-solid fa-copy me-1"></i>{{ scanCopied ? $t('modal.scanCopied') : $t('modal.scanCopy') }}
                    </button>
                </div>
            </template>
        </barcode-scanner>

        <div class="mb-3">
            <label for="profileSelect" class="form-label"><small>{{ $t('modal.workType') }}</small></label>
            <select id="profileSelect" v-model="selectedProfile" class="form-select bg-body-tertiary">
                <option v-for="profile in profiles" :key="profile.id" :value="profile.id">
                    {{ profile.name }}
                </option>
            </select>
        </div>

        <div class="mb-3" v-if="type === 'materials'">
            <label for="descriptionInput" class="form-label"><small>{{ $t('modal.descriptionForClient') }}</small></label>
            <input id="descriptionInput" class="form-control bg-body-tertiary" v-model="description" :placeholder="$t('taskDetail.descriptionPlaceholder')"
                @input="fetchSuggestions" list="productList" />
            <datalist id="productList">
                <option v-for="product in suggestions" :key="product.id" :value="product.name"></option>
            </datalist>
        </div>
        <div class="mb-3" v-else>
            <label for="descriptionInput" class="form-label"><small>{{ $t('modal.descriptionForClient') }}</small></label>
            <textarea id="descriptionInput" class="form-control bg-body-tertiary" v-model="description"
                :placeholder="$t('taskDetail.descriptionPlaceholder')"></textarea>
        </div>

        <div class="mb-3">
            <label for="notesInput" class="form-label"><small>{{ $t('modal.internalNote') }}</small></label>
            <textarea id="notesInput" class="form-control bg-body-tertiary" v-model="notes"
                :placeholder="$t('modal.internalNotePlaceholder')"></textarea>
        </div>

        <div class="mb-3">
            <label class="form-label">
                <small>{{ $t('task.date') }}</small>
            </label>
            <VueDatePicker v-model="issued_on" format="dd.MM.yyyy" date-picker auto-apply
                input-class-name="bg-body-tertiary" :locale="locale" :clearable="false" required teleport-center />

        </div>

        <div class="mb-3">
            <label v-if="type === 'materials'" class="form-label">
                <small>{{ $t('modal.quantity') }}</small>
            </label>

            <div v-if="type === 'materials'">
                <input id="amountInput" class="form-control bg-body-tertiary" v-model="amount"
                    :placeholder="$t('modal.quantity')" type="text" inputmode="decimal" pattern="[0-9]+([,][0-9]{1,2})?"
                    @input="validateAmountInput('amount', $event.target.value)" />
            </div>

            <div v-else>
                <div class="d-flex">
                    <div class="me-2">
                        <label for="hoursSelect" class="form-label"><small>{{ $t('modal.hours') }}</small></label>
                        <select id="hoursSelect" class="form-select bg-body-tertiary" v-model.number="selectedHours"
                            :disabled="isClosedAmount" @change="snapMinutesToStep">
                            <option v-for="hour in workHoursOptions" :key="hour" :value="hour">
                                {{ hour }}
                            </option>
                        </select>
                    </div>

                    <div>
                        <label for="minutesSelect" class="form-label"><small>{{ $t('modal.minutes') }}</small></label>
                        <select id="minutesSelect" class="form-select bg-body-tertiary" v-model.number="selectedMinutes"
                            :disabled="isClosedAmount">
                            <option v-for="minute in workMinutesOptions" :key="minute" :value="minute">
                                {{ minute }}
                            </option>
                        </select>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="type === 'hours'" class="mb-3">
            <label for="pointsInput" class="form-label"><small>{{ $t('modal.points') }}</small></label>
            <input id="pointsInput" class="form-control bg-body-tertiary" v-model="points" :placeholder="$t('modal.points')" type="text"
                inputmode="decimal" pattern="[0-9]+([,][0-9]{1,2})?"
                @input="validateAmountInput('points', $event.target.value)" />
        </div>

        <div v-if="type === 'hours'" class="mb-3">

            <div>
                <div class="d-flex">
                    <div class="me-2">
                        <label for="distanceHoursSelect" class="form-label"><small>{{ $t('modal.travelHours') }}</small></label>
                        <select id="distanceHoursSelect" class="form-select bg-body-tertiary"
                            v-model.number="selectedDistanceHours">
                            <option v-for="hour in hoursOptions" :key="hour" :value="hour">
                                {{ hour }}
                            </option>
                        </select>
                    </div>
                    <div>
                        <label for="distanceMinutesSelect" class="form-label"><small>{{ $t('modal.minutes') }}</small></label>
                        <select id="distanceMinutesSelect" class="form-select bg-body-tertiary"
                            v-model.number="selectedDistanceMinutes">
                            <option v-for="minute in minutesOptions" :key="minute" :value="minute">
                                {{ minute }}
                            </option>
                        </select>
                    </div>
                </div>
            </div>
        </div>

        <div class="mb-3" v-if="isCustomResource">
            <label for="priceInput" class="form-label"><small>{{ $t('modal.price') }}</small></label>
            <input v-model="price" id="priceInput" class="form-control bg-body-tertiary" :placeholder="$t('modal.price')" type="text"
                inputmode="decimal" pattern="[0-9]+([,][0-9]{1,2})?"
                @input="validateAmountInput('price', $event.target.value)">
        </div>

        <div class="row">
            <div class="col">
                <div v-if="!lockAccountingStatus" class="mb-3 form-check">
                    <input type="checkbox" v-model="accountingStatusId" class="form-check-input"
                        id="accountingCheckbox">
                    <label class="form-check-label" for="accountingCheckbox">{{ $t('modal.toInvoice') }}</label>
                </div>
            </div>
            <div class="col text-end">
                <span v-if="this.itemToEdit && !actionInProgress" role="button" @click="deleteItem" class="text-danger"><small><i
                            class="fa-solid fa-trash-can me-2"></i>{{ $t('common.delete') }}</small></span>
            </div>
        </div>


        <div class="row mt-3">
            <div class="col">
                <button @click="$emit('close')" class="btn btn-outline-primary w-100" :disabled="actionInProgress">{{ $t('common.cancel') }}</button>
            </div>
            <div class="col">
                <button v-if="!this.itemToEdit" @click="submitItem" class="btn btn-primary w-100" :disabled="actionInProgress">{{ $t('common.save') }}</button>
                <button v-if="this.itemToEdit" @click="saveChanges" class="btn btn-primary w-100" :disabled="actionInProgress">{{ $t('common.save') }}</button>
            </div>
        </div>

    </div>
</template>

<script>

import axios from '@/services/axios';
import VueSelect from 'vue-select';
import VueDatePicker from '@vuepic/vue-datepicker';
import 'vue-select/dist/vue-select.css';
import { useI18n } from 'vue-i18n';
import BarcodeScanner from '@/components/BarcodeScanner.vue';

/**
 * What the "Arveks" checkbox starts at for a NEW item. Materials always default
 * to going on the invoice; hours follow the task's own accounting status.
 */
function defaultAccountingStatus(type, taskAccountingStatus) {
    return type === 'materials' ? true : taskAccountingStatus;
}

// The API's 409 messages are English. The two we know about are translated;
// anything else falls back to the server's own wording, which is still more
// use than a generic failure.
const STOCK_CONFLICT_KEYS = {
    'Stock level too low': 'modal.stockTooLow',
    'Line already moved to warehouse': 'modal.lineAlreadyMoved',
};

export default {
    components: {
        'v-select': VueSelect,
        VueDatePicker,
        BarcodeScanner
    },
    setup() {
        const { locale } = useI18n();
        return { locale };
    },
    props: {
        taskId: {
            type: Number,
            required: true
        },
        profileId: {
            type: Number
        },
        priorityId: {
            type: Number,
            required: true
        },
        accountingstatus_id: {
            type: Boolean,
            required: true
        },
        type: {
            type: String,
            required: true
        },
        itemToEdit: {
            type: Object,
            default: () => ({})
        },
        preselectResource: {
            type: Object,
            default: null
        },
    },

    data() {
        return {
            selectedResource: null,
            selectedProfile: this.profileId,
            amount: 1,
            distance: null,
            price: null,
            points: null,
            issued_on: new Date(),
            description: null,
            // Empty string, never null: the API treats a null note as "leave untouched"
            notes: '',
            accountingStatusId: defaultAccountingStatus(this.type, this.accountingstatus_id),
            isCustomResource: false,
            // Hours only: the resource fixes the amount to its default_amount
            isClosedAmount: false,
            // Hours only: a non-closed resource with a default_amount allows only
            // multiples of it. Its length in minutes, or null for no step.
            stepMinutes: null,
            resources: [],
            profiles: [],
            scannerVisible: false,
            // A scanned serial that matched no stock item. Kept so the scanner
            // can show it and offer to copy it, instead of a toast that vanishes.
            scanNoMatch: '',
            scanCopied: false,
            stock_item_id: null,
            resource_id: null,
            selectedHours: 0,
            selectedMinutes: 0,
            selectedDistanceHours: 0,
            selectedDistanceMinutes: 0,
            defaultMaxHours: 200, // Default maximum hours
            hoursOptions: [], // Will be initialized dynamically
            minutesOptions: [0, 15, 30, 45], // Will be initialized dynamically
            suggestions: [],
            lastEmptyInputLength: null, // New property
            // Held only while the request is in flight, so a slow network
            // cannot turn one click into two rows.
            actionInProgress: false,
        };
    },
    watch: {
        selectedResource(newValue) {
            const resource = this.resources.find(r => r.id === newValue);
            if (resource && resource.is_custom_resource) {
                this.isCustomResource = true;
            } else {
                this.isCustomResource = false;
            }
            // When editing, setEditData has already loaded the saved amount;
            // this watcher runs after it and must not replace it with the default.
            this.applyDefaultAmount(resource, { useDefault: !this.itemToEdit });
            if (resource && resource.stock_item_id) {
                this.stock_item_id = resource.stock_item_id;
            } else {
                this.stock_item_id = null;
            }
            if (resource && resource.resource_id) {
                this.resource_id = resource.resource_id;
            } else {
                this.resource_id = null;
            }
        },
        selectedHours() {
            this.updateAmountFromTime();
        },
        selectedMinutes() {
            this.updateAmountFromTime();
        },
        amount() {
            if (this.type !== 'materials') {
                this.updateTimeFromAmount();
            }
        },
        selectedDistanceHours() {
            this.updateDistanceFromTime();
        },
        selectedDistanceMinutes() {
            this.updateDistanceFromTime();
        },
        distance(newVal) {
            if (newVal != null) {
                this.updateTimeFromDistance();
            }
        },
    },
    mounted() {
        this.fetchResources().then(() => {
            this.fetchProfiles().then(() => {
                if (this.itemToEdit) {
                    this.setEditData();
                } else if (this.preselectResource) {
                    const match = this.resources.find(r =>
                        (this.preselectResource.stock_item_id && r.id === this.preselectResource.stock_item_id) ||
                        (this.preselectResource.resource_id && r.resource_id === this.preselectResource.resource_id)
                    );
                    if (match) this.selectedResource = match.id;
                    // Inherit the planned row's price. Set unconditionally: the
                    // field only shows for open-price resources, but the user can
                    // switch the dropdown to one after the fact.
                    this.price = this.preselectResource.price;
                }
                if (this.type !== 'materials' && this.amount != null) {
                    this.updateTimeFromAmount();
                }
                if (this.distance != null) {
                    this.updateTimeFromDistance();
                }
            });
        });
    },
    methods: {
        // Stock items carry a resource_code the technician may know better than
        // the name. Match on it too, while the list itself never shows the code.
        openScanner() {
            this.scanNoMatch = '';
            this.scanCopied = false;
            this.scannerVisible = true;
        },

        closeScanner() {
            this.scannerVisible = false;
            this.scanNoMatch = '';
            this.scanCopied = false;
        },

        // A decoded serial selects the matching stock item; it is never stored as
        // free text. Matching is exact against `sn` (case-insensitive, trimmed) so
        // a scan can never land on the wrong item.
        onScannedValue(scanned) {
            const needle = scanned.toLocaleLowerCase();
            const match = this.resources.find(r => {
                const sn = (r.sn ?? '').toString().trim();
                return sn && sn.toLocaleLowerCase() === needle;
            });

            if (match) {
                // Select FIRST. This is the outcome the technician came for, and
                // it used to sit behind an awaited clipboard write that commonly
                // rejects -- and can hang pending while the document is not
                // focused. Nothing courteous gets to block it any more.
                //
                // The selectedResource watcher derives stock_item_id, resource_id
                // and isCustomResource, so this behaves like a hand-picked item.
                this.selectedResource = match.id;
                this.closeScanner();
                this.$root.$refs.globalAlert?.showAlert(this.$t('modal.scanMatched', { sn: scanned }));
                return;
            }

            // No match: hold the camera open with the serial on screen. A toast
            // would be hidden behind this modal, and reopening the camera to
            // re-read a number the phone already had is the wrong ask.
            this.scanNoMatch = scanned;
            this.scanCopied = false;
            this.$refs.scanner?.resume(scanned);
        },

        // Deliberately driven by a button rather than fired from the scan
        // callback: a clipboard write outside a user gesture is refused outright
        // on iOS Safari and by default in Firefox, so the old automatic copy was
        // a promise the UI could not keep.
        async copyScanned() {
            try {
                await navigator.clipboard.writeText(this.scanNoMatch);
                this.scanCopied = true;
            } catch (error) {
                console.warn('Could not copy scanned serial to clipboard:', error);
            }
        },

        filterResource(option, label, search) {
            const needle = search.trim().toLocaleLowerCase();
            if (!needle) return true;
            return [label, option.resource_code]
                .some(field => (field ?? '').toString().toLocaleLowerCase().includes(needle));
        },
        // Quantity arrives as a decimal string ("3.00"); trim it for display.
        formatQuantity(quantity) {
            return parseFloat(quantity).toString();
        },
        fetchSuggestions() {
            if (this.description.length < 2) {
                this.suggestions = [];
                this.lastEmptyInputLength = null; // Reset when input is too short
                return;
            }
            if (
                this.lastEmptyInputLength !== null &&
                this.description.length > this.lastEmptyInputLength &&
                this.description.startsWith(this.description.slice(0, this.lastEmptyInputLength))
            ) {
                // Do not make API call
                return;
            }
            axios
                .get(`/resources`, {
                    params: {
                        is_material: 1,
                        not_active: 0,
                        search: this.description,
                    },
                })
                .then((response) => {
                    this.suggestions = response.data.data;
                    if (this.suggestions.length === 0) {
                        // No results found; set the last empty input length
                        this.lastEmptyInputLength = this.description.length;
                    } else {
                        // Reset the last empty input length
                        this.lastEmptyInputLength = null;
                    }
                })
                .catch((error) => {
                    console.error(error);
                });
        },
        updateDistanceFromTime() {
            this.distance = this.selectedDistanceHours + this.selectedDistanceMinutes / 60;
        },

        updateTimeFromDistance() {
            const totalMinutes = Math.round(this.distance * 60);
            this.selectedDistanceHours = Math.floor(totalMinutes / 60);
            this.selectedDistanceMinutes = totalMinutes % 60;
            if (!this.hoursOptions.includes(this.selectedDistanceHours)) {
                const maxHour = Math.max(this.defaultMaxHours, this.selectedDistanceHours);
                this.hoursOptions = Array.from({ length: maxHour + 1 }, (_, i) => i);
            }

            if (!this.minutesOptions.includes(this.selectedDistanceMinutes)) {
                this.minutesOptions.push(this.selectedDistanceMinutes);
                this.minutesOptions.sort((a, b) => a - b);
            }
        },


        // A resource's default_amount shapes the time it allows. A closed-amount
        // resource locks the time to it; any other resource with a default only
        // allows multiples of it. Without a default the time is free.
        applyDefaultAmount(resource, { useDefault }) {
            const hasDefault = this.type !== 'materials' && resource?.default_amount != null;
            this.isClosedAmount = hasDefault && !!resource.is_closed_amount;
            const step = hasDefault && !this.isClosedAmount
                ? Math.round(resource.default_amount * 60)
                : 0;
            this.stepMinutes = step > 0 ? step : null;
            if ((this.isClosedAmount || this.stepMinutes) && useDefault) {
                // The amount watcher moves the hour/minute selects to match
                this.amount = resource.default_amount;
            }
        },

        // Minutes that keep the total on the step for a given hour. Zero is
        // never a valid total, so 0h starts at one step.
        stepMinutesFor(hour) {
            return Array.from({ length: 60 }, (_, m) => m).filter(m => {
                const total = hour * 60 + m;
                return total > 0 && total % this.stepMinutes === 0;
            });
        },

        // Runs only when the user picks an hour, so an existing line saved off
        // the step keeps its value until the user changes it.
        snapMinutesToStep() {
            if (!this.stepMinutes) return;
            const valid = this.stepMinutesFor(this.selectedHours);
            if (!valid.length || valid.includes(this.selectedMinutes)) return;
            this.selectedMinutes = valid.reduce((best, m) =>
                Math.abs(m - this.selectedMinutes) < Math.abs(best - this.selectedMinutes) ? m : best);
        },

        updateAmountFromTime() {
            this.amount = this.selectedHours + this.selectedMinutes / 60;
        },

        updateTimeFromAmount() {
            const totalMinutes = Math.round(this.amount * 60);
            this.selectedHours = Math.floor(totalMinutes / 60);
            this.selectedMinutes = totalMinutes % 60;
            if (!this.hoursOptions.includes(this.selectedHours)) {
                const maxHour = Math.max(this.defaultMaxHours, this.selectedHours);
                this.hoursOptions = Array.from({ length: maxHour + 1 }, (_, i) => i);
            }

            if (!this.minutesOptions.includes(this.selectedMinutes)) {
                this.minutesOptions.push(this.selectedMinutes);
                this.minutesOptions.sort((a, b) => a - b);
            }
        },

        validateAmountInput(propertyName, inputValue) {
            let parsedValue = inputValue.replace(/,/g, '.');
            parsedValue = parsedValue.replace(/[^0-9.]/g, '');
            this[propertyName] = parsedValue;
        },
        /** What to show the user when adding or saving a line fails. */
        resourceErrorMessage(error, fallbackKey) {
            const serverMessage = error?.response?.data?.error?.message;
            const translated = STOCK_CONFLICT_KEYS[serverMessage];
            if (translated) return this.$t(translated);
            return serverMessage || this.$t(fallbackKey);
        },


        async fetchResources() {
            try {
                /* ---------- 1) build the request ----------------------- */
                let url,                     // base endpoint
                    params = {};             // query‑string params object

                if (this.type === 'materials') {
                    url = `/users/${this.currentUser.id}/stock-items`;
                    // (add other stock‑item params here, if any)
                } else {
                    url = '/resources';
                    params = { is_material: 0, not_active: 0 };
                }

                /* ---------- 3) fire the request ------------------------- */
                const { data } = await axios.get(url, { params });
                this.resources = data.data;              // populate <v-select>
            } catch (error) {
                console.error('Error fetching resources:', error);
            }
        },
        async fetchProfiles() {
            try {
                const response = await axios.get(`/profiles?not_active=0`);
                this.profiles = response.data.data;
            } catch (error) {
                console.error('Error fetching profiles:', error);
            }
        },
        setEditData() {
            this.selectedResource = this.itemToEdit.resource_id;
            this.selectedProfile = this.itemToEdit.profile_id;
            this.amount = this.itemToEdit.amount;
            this.points = this.itemToEdit.points;
            this.distance = this.itemToEdit.distance;
            this.price = this.itemToEdit.price;
            this.description = this.itemToEdit.description;
            // A cleared note reads back as null from the app but "" from the web app
            this.notes = this.itemToEdit.notes ?? '';
            this.accountingStatusId = this.itemToEdit.accountingstatus_id;
            this.issued_on = (new Date(this.itemToEdit.issued_on));

            const resource = this.resources.find(r => r.id === this.selectedResource);
            this.isCustomResource = resource ? resource.is_custom_resource : false;
            this.applyDefaultAmount(resource, { useDefault: false });
            this.stock_item_id = resource ? resource.stock_item_id : null;
            this.resource_id = resource ? resource.resource_id : null;
            if (this.type !== 'materials') {
                this.updateTimeFromAmount();
            }
            if (this.distance != null) {
                this.updateTimeFromDistance();
            }
        },
        async submitItem() {
            if (!this.resource_id) {
                this.$root.$refs.globalAlert.showAlert(this.$t('modal.productRequired'), 'danger');
                return;
            }
            if (this.actionInProgress) return;
            this.actionInProgress = true;
            try {
                const formattedDate = `${this.issued_on.getFullYear()}-${(this.issued_on.getMonth() + 1).toString().padStart(2, '0')}-${this.issued_on.getDate().toString().padStart(2, '0')}`;

                const payload = {
                    resource_id: this.resource_id,
                    profile_id: this.selectedProfile,
                    amount: parseFloat(this.amount),
                    points: parseFloat(this.points),
                    distance: parseFloat(this.distance),
                    description: this.description,
                    notes: this.notes || '',
                    price: this.isCustomResource ? parseFloat(this.price) : null,
                    accountingstatus_id: this.accountingStatusId ? true : false,
                    stock_item_id: this.stock_item_id,
                    issued_on: formattedDate,
                    parent_id: this.preselectResource ? this.preselectResource.id : null,
                };

                const response = await axios.post(`/tasks/${this.taskId}/resources`, payload);

                if (response.status === 200) {
                    this.$emit('item-added', response.data.data, this.preselectResource ? this.preselectResource.id : null);
                    this.resetModalFields();
                    this.$emit('close');
                }
            } catch (error) {
                console.error('Error saving item:', error);
                this.$root.$refs.globalAlert.showAlert(
                    this.resourceErrorMessage(error, 'modal.addFailed'),
                    'danger'
                );
            } finally {
                this.actionInProgress = false;
            }
        },
        async saveChanges() {
            const parsedAmount = parseFloat(this.amount);
            // An emptied field parses to NaN, which serialises as null — and the
            // API reads null as "leave unchanged", so the edit would look saved
            // while doing nothing. Hours come from the selects and may be 0.
            if (this.type === 'materials' && !(parsedAmount > 0)) {
                this.$root.$refs.globalAlert.showAlert(this.$t('modal.quantityRequired'), 'danger');
                return;
            }
            const formattedDate = `${this.issued_on.getFullYear()}-${(this.issued_on.getMonth() + 1).toString().padStart(2, '0')}-${this.issued_on.getDate().toString().padStart(2, '0')}`;
            const payload = {
                resource_id: this.resource_id,
                profile_id: this.selectedProfile,
                amount: parsedAmount,
                points: this.points,
                distance: this.distance,
                description: this.description,
                // "" clears the note; null would silently leave it untouched
                notes: this.notes || '',
                issued_on: formattedDate,
                price: this.isCustomResource ? this.price : null,
                accountingstatus_id: this.accountingStatusId ? true : false,
                stock_item_id: this.stock_item_id,
            };

            if (this.actionInProgress) return;
            this.actionInProgress = true;
            try {
                const response = await axios.patch(`/tasks/${this.taskId}/resources/${this.itemToEdit.id}`, payload);
                if (response.status === 200) {
                    this.$emit('item-updated', response.data.data);
                    this.resetModalFields();
                    this.$emit('close');
                }
            } catch (error) {
                console.error('Error updating item:', error);
                this.$root.$refs.globalAlert.showAlert(
                    this.resourceErrorMessage(error, 'common.saveFailed'),
                    'danger'
                );
            } finally {
                this.actionInProgress = false;
            }
        },
        async deleteItem() {
            if (this.actionInProgress) return;
            this.actionInProgress = true;
            try {
                await axios.delete(`/tasks/${this.taskId}/resources/${this.itemToEdit.id}`);
                this.$emit('item-deleted', this.itemToEdit.id);
                this.$emit('close');
            } catch (error) {
                console.error('Error deleting item:', error);
            } finally {
                this.actionInProgress = false;
            }
        },
        resetModalFields() {
            this.selectedResource = null;
            this.selectedProfile = null;
            this.amount = 1;
            this.points = null;
            this.distance = null;
            this.price = null;
            this.issued_on = new Date();
            this.description = null;
            this.notes = '';
            this.accountingStatusId = defaultAccountingStatus(this.type, this.accountingstatus_id);
            this.stock_item_id = null;
            this.resource_id = null;
            this.isClosedAmount = false;
            this.stepMinutes = null;
            this.selectedHours = 0;
            this.selectedMinutes = 0;
            this.selectedDistanceHours = 0;
            this.selectedDistanceMinutes = 0;
        }
    },
    computed: {
        // Priority-1 hours always follow the task's accounting status
        lockAccountingStatus() {
            return this.type === 'hours' && Number(this.priorityId) === 1;
        },
        // The work-time selects. The travel-time selects keep using the plain
        // hoursOptions/minutesOptions. The current value is always kept, so a
        // line saved off the step still shows what it holds.
        workHoursOptions() {
            if (!this.stepMinutes) return this.hoursOptions;
            return this.hoursOptions.filter(h =>
                h === this.selectedHours || this.stepMinutesFor(h).length > 0);
        },
        workMinutesOptions() {
            if (!this.stepMinutes) return this.minutesOptions;
            const valid = this.stepMinutesFor(this.selectedHours);
            if (!valid.includes(this.selectedMinutes)) {
                valid.push(this.selectedMinutes);
                valid.sort((a, b) => a - b);
            }
            return valid;
        },
        currentUser() {
            const user = localStorage.getItem('user');
            return user ? JSON.parse(user) : null;
        }
    },
};
</script>

